import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { FieldMetadataDisplay } from "@/pages/applications/pages/customFields/components/FieldMetadataDisplay";

type CustomField = InterludeTypes["CustomField"];

interface FormValues {
  confirmName: string;
}

interface ArchiveFieldModalProps {
  open: boolean;
  field: CustomField | null;
  onClose: () => void;
  onSuccess: () => void;
}

const styles = {
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  warningList: {
    margin: 0,
    paddingLeft: "20px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const ArchiveFieldModal: React.FC<ArchiveFieldModalProps> =
  function ArchiveFieldModal({ open, field, onClose, onSuccess }) {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: {
        confirmName: "",
      },
    });

    const watchedConfirmName = watch("confirmName");

    // Reset form when field changes
    useEffect(() => {
      if (field) {
        reset({
          confirmName: "",
        });
      }
    }, [field, reset]);

    const archiveMutation = interlude.customField.useArchiveCustomField(
      {
        params: {
          fieldId: field?.id ?? 0,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
      },
    );

    const handleClose = useHandler(() => {
      reset();
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler(() => {
      if (!field) return;
      archiveMutation.mutate(undefined);
    });

    const isConfirmNameMatch = useMemo(() => {
      if (!field) return false;
      return watchedConfirmName.trim() === field.name.trim();
    }, [watchedConfirmName, field]);

    const footer: React.ReactNode = useMemo(
      () => (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={handleClose}>
            {t("customField.archiveModal.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit(onSubmit)}
            disabled={!isConfirmNameMatch || !field}
            loading={archiveMutation.isPending}
          >
            {t("customField.archiveModal.submit")}
          </Button>
        </Stack>
      ),
      [
        handleClose,
        handleSubmit,
        onSubmit,
        isConfirmNameMatch,
        field,
        archiveMutation.isPending,
        t,
      ],
    );

    if (!field) {
      return null;
    }

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("customField.archiveModal.title")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("customField.archiveModal.description")}
          </Typography>

          {/* Warning InfoBox */}
          <InfoBox
            variant="warning"
            title={t("customField.archiveModal.warning.title")}
            description={
              <Box component="ul" sx={styles.warningList}>
                <li>
                  <Typography variant="body" color="inherit">
                    {t("customField.archiveModal.warning.keyNotReusable")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body" color="inherit">
                    {t("customField.archiveModal.warning.dataPreserved")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body" color="inherit">
                    {t("customField.archiveModal.warning.canRestore")}
                  </Typography>
                </li>
              </Box>
            }
          />

          {/* Field Metadata Display */}
          <FieldMetadataDisplay
            field={field}
            translationKeyPrefix="customField.archiveModal"
          />

          {/* Confirmation Input */}
          <Box sx={styles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("customField.archiveModal.confirmLabel")}
            </Typography>
            <Controller
              name="confirmName"
              control={control}
              render={({ field: formField }) => (
                <TextField
                  {...formField}
                  fullWidth
                  placeholder={t(
                    "customField.archiveModal.confirmPlaceholder",
                    {
                      fieldName: field.name,
                    },
                  )}
                />
              )}
            />
          </Box>
        </Stack>
      </Modal>
    );
  };
